import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Card, { Item } from '../components/Card'
import SidePanel from '../components/SidePanel'
import EmailRequestForm from '../components/EmailRequestForm'
import { useDarkMode } from '../App'
import { getFeaturedBooks } from '../lib/api'
import { useTranslation } from '../i18n/translations'
import { useLanguage } from '../contexts/LanguageContext'

export default function Home(){
  const [sel, setSel] = useState<Item|null>(null)
  const [featuredBooks, setFeaturedBooks] = useState<Item[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { isDark } = useDarkMode()
  const { t } = useTranslation()
  const { isRTL } = useLanguage()
  const open = !!sel

  // Fetch featured books on component mount
  useEffect(() => {
    const loadFeaturedBooks = async () => {
      try {
        setIsLoading(true)
        const books = await getFeaturedBooks(12)
        setFeaturedBooks(books as Item[])
      } catch (error) {
        console.error('Failed to load featured books:', error)
        // Fallback to empty array on error
        setFeaturedBooks([])
      } finally {
        setIsLoading(false)
      }
    }

    loadFeaturedBooks()
  }, [])

  return (
    <div className={`w-full max-w-full overflow-x-hidden transition-colors ${isDark ? 'text-white' : 'text-gray-900'} ${isRTL ? 'font-mixed rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className={`absolute inset-0 transition-colors ${
          isDark ? 'bg-gradient-to-br from-emerald-900/20 to-cyan-900/20' : 'bg-gradient-to-br from-blue-50/50 to-purple-50/50'
        }`}></div>
        <div className={`relative max-w-7xl mx-auto px-4 w-full overflow-x-hidden ${isRTL ? 'pt-40 pb-20' : 'py-20'}`}>
          <div className={`text-center ${isRTL ? 'rtl' : ''}`}>
            <div className={`inline-flex items-center px-4 py-2 border rounded-full text-sm font-medium mb-8 transition-colors ${
              isDark 
                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' 
                : 'bg-blue-50 border-blue-200 text-blue-600'
            } ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span className={`w-2 h-2 rounded-full animate-pulse ${
                isDark ? 'bg-emerald-400' : 'bg-blue-500'
              } ${isRTL ? 'ml-2 mr-0' : 'mr-2 ml-0'}`}></span>
              {t('home.digital_knowledge_hub')}
            </div>
            
            <h1 className={`text-5xl md:text-6xl font-bold mb-6 transition-colors ${
              isDark 
                ? 'bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent' 
                : 'text-gray-900'
            }`}>
              {t('home.title')}
            </h1>
            <p className={`text-xl mb-12 max-w-2xl mx-auto transition-colors ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {t('home.subtitle')}
            </p>
            
            {/* Browse Collection Button */}
            <div className="mb-16">
              <Link
                to="/search"
                className={`inline-flex items-center px-8 py-4 rounded-xl text-white font-semibold transition-all transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isDark 
                    ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500' 
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                } shadow-lg hover:shadow-xl ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <svg className={`w-5 h-5 ${isRTL ? 'ml-2 mr-0' : 'mr-2 ml-0'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {t('home.browse_collection')}
              </Link>
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              <div className={`border rounded-xl p-6 text-center transition-colors ${
                isDark 
                  ? 'bg-gray-800/50 border-gray-700/50' 
                  : 'bg-white border-gray-200 shadow-sm'
              }`}>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4 ${
                  isDark ? 'bg-emerald-500/20' : 'bg-blue-100'
                }`}>
                  <svg className={`w-6 h-6 ${isDark ? 'text-emerald-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('home.features.books_count')}</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('home.features.books_desc')}</p>
              </div>
              <div className={`border rounded-xl p-6 text-center transition-colors ${
                isDark 
                  ? 'bg-gray-800/50 border-gray-700/50' 
                  : 'bg-white border-gray-200 shadow-sm'
              }`}>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4 ${
                  isDark ? 'bg-cyan-500/20' : 'bg-green-100'
                }`}>
                  <svg className={`w-6 h-6 ${isDark ? 'text-cyan-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('home.features.research_title')}</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('home.features.research_desc')}</p>
              </div>
              <div className={`border rounded-xl p-6 text-center transition-colors ${
                isDark 
                  ? 'bg-gray-800/50 border-gray-700/50' 
                  : 'bg-white border-gray-200 shadow-sm'
              }`}>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4 ${
                  isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                }`}>
                  <svg className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('home.features.access_title')}</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('home.features.access_desc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Section */}
      <div className="max-w-7xl mx-auto px-4 py-16 w-full overflow-x-hidden">
        <h2 className={`text-2xl font-bold mb-8 text-center ${isRTL ? 'font-mixed' : ''}`}>{t('home.featured_resources')}</h2>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading featured books...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredBooks.map(it => (
              <Card key={it.slug} item={it} onClick={() => setSel(it)} />
            ))}
          </div>
        )}
      </div>

      <SidePanel open={open} onClose={() => setSel(null)} title={sel?.title || ''}>
        {sel && (
          <div className="space-y-4">
            {/* Cover Image */}
            {sel.coverUrl && (
              <div className="w-full h-64 rounded-lg overflow-hidden">
                <img src={sel.coverUrl} alt={sel.title} className="w-full h-full object-cover"/>
              </div>
            )}
            
            {/* Metadata */}
            <div className={`space-y-2 ${isDark ? 'text-gray-300' : 'text-gray-600'} ${isRTL ? 'text-right' : ''}`}>
              <div className="text-sm">
                <strong>{t('item.authors')}:</strong> {sel.authors?.join(', ') || 'Unknown'}
              </div>
              <div className="text-sm">
                <strong>{t('item.year')}:</strong> {sel.year}
              </div>
              <div className="text-sm">
                <strong>{t('item.type')}:</strong> {sel.type === 'book' ? t('common.book') : t('common.paper')}
              </div>
              <div className="text-sm">
                <strong>{t('item.license')}:</strong> 
                <span className={`${isRTL ? 'mr-2' : 'ml-2'} px-2 py-0.5 rounded text-xs ${
                  isDark 
                    ? 'bg-emerald-500/20 text-emerald-400' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {sel.license}
                </span>
              </div>
              {sel.fileSize && (
                <div className="text-sm">
                  <strong>File Size:</strong> {sel.fileSize}
                </div>
              )}
              {sel.fileType && (
                <div className="text-sm">
                  <strong>Format:</strong> {sel.fileType}
                </div>
              )}
            </div>

            {/* Description */}
            {sel.description && (
              <div className={`p-4 rounded-lg ${
                isDark ? 'bg-gray-800/50' : 'bg-gray-50'
              } ${isRTL ? 'text-right' : ''}`}>
                <h3 className="font-semibold mb-2">{t('item.description')}</h3>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {sel.description}
                </p>
              </div>
            )}

            {/* Download Form */}
            <EmailRequestForm itemId={sel.slug} />
          </div>
        )}
      </SidePanel>
    </div>
  )
}